// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput, PasswordInput, Checkbox } from '@/components/ui/inputs';
import { IconUser, IconMail, IconLock, IconArrowLeft, Icon3dCubeSphere } from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';
import { Floating3DCard, SocialLoginButtons, PasswordStrength } from '../components';

export default function RegisterMinimal3D() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Register attempt:', { name, email, password });
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(180deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(180deg, ${theme.colors.gray[0]} 0%, ${theme.colors.gray[1]} 100%)`,
        position: 'relative',
      }}
    >
      {/* Subtle pattern */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${isDark ? theme.colors.dark[6] : theme.colors.gray[3]} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="xl" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <FadeIn delay={100}>
            <Floating3DCard
              maxTilt={6}
              floatingShadow
              glow
              glowColor={theme.colors.teal[5]}
              radius="lg"
            >
              <Stack gap="lg">
                {/* Back button */}
                <Button
                  component={Link}
                  to="/app/demo/auth-pages/register"
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  w="fit-content"
                >
                  Back to variants
                </Button>

                {/* Header */}
                <Stack align="center" gap="xs">
                  <Badge
                    leftSection={<Icon3dCubeSphere size={12} />}
                    variant="gradient"
                    gradient={{ from: 'gray', to: 'dark' }}
                    size="lg"
                  >
                    Minimal 3D
                  </Badge>
                  <Title order={2} ta="center" mt="md">
                    Get Started
                  </Title>
                  <Text c="dimmed" size="sm" ta="center">
                    Create your account in seconds
                  </Text>
                </Stack>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <TextInput
                      label="Full name"
                      placeholder="John Doe"
                      leftSection={<IconUser size={16} />}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      size="md"
                      radius="md"
                      variant="filled"
                    />

                    <TextInput
                      label="Email"
                      placeholder="your@email.com"
                      leftSection={<IconMail size={16} />}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      size="md"
                      radius="md"
                      variant="filled"
                    />

                    <div>
                      <PasswordInput
                        label="Password"
                        placeholder="Create a password"
                        leftSection={<IconLock size={16} />}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        variant="filled"
                      />
                      {password && <PasswordStrength password={password} variant="bar" />}
                    </div>

                    <PasswordInput
                      label="Confirm password"
                      placeholder="Confirm your password"
                      leftSection={<IconLock size={16} />}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      size="md"
                      radius="md"
                      variant="filled"
                      error={confirmPassword && password !== confirmPassword ? "Passwords don't match" : undefined}
                    />

                    <Checkbox
                      label={
                        <Text size="sm">
                          I agree to the <Anchor size="sm">Terms</Anchor> and{' '}
                          <Anchor size="sm">Privacy</Anchor>
                        </Text>
                      }
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      radius="md"
                      color="dark"
                      disabled={!acceptTerms}
                    >
                      Create account
                    </Button>
                  </Stack>
                </form>

                {/* Social Login */}
                <SocialLoginButtons
                  providers={['google', 'github', 'apple']}
                  layout="icons-only"
                  dividerLabel="or"
                />

                {/* Login Link */}
                <Text ta="center" size="sm">
                  Already have an account?{' '}
                  <Anchor component={Link} to="/app/demo/auth-pages/login/minimal3d" fw={500}>
                    Sign in
                  </Anchor>
                </Text>
              </Stack>
            </Floating3DCard>
          </FadeIn>
        </Container>
      </Center>
    </Box>
  );
}
